import topicToSubtopicMetadata from './topicToSubtopicMetadata.json';

export type SubtopicOption = { value: string; label: string };

type RawSubtopicEntry = string | { value: string; label?: string };

const meta = topicToSubtopicMetadata as {
  subtopicsByTopicId: Record<string, RawSubtopicEntry[] | unknown>;
  subtopicLabelsByValue: Record<string, string>;
};

const TOPIC_IDS_WITH_SUBTOPICS = new Set(Object.keys(meta.subtopicsByTopicId));

/** Supports v4 string[] and legacy { value, label }[] in JSON. */
function rawSubtopicsToOptions(raw: unknown): SubtopicOption[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const labels = meta.subtopicLabelsByValue;
  return raw.map((item: RawSubtopicEntry) => {
    if (typeof item === 'string') {
      const value = item;
      return { value, label: labels[value] ?? value };
    }
    if (item && typeof item === 'object' && 'value' in item) {
      const value = String((item as { value: string }).value);
      return { value, label: labels[value] ?? value };
    }
    return { value: String(item), label: String(item) };
  });
}

export function topicHasSubtopics(
  topicInternalId: number | null | undefined
): boolean {
  if (topicInternalId == null) return false;
  return TOPIC_IDS_WITH_SUBTOPICS.has(String(topicInternalId));
}

export function getSubtopicsForTopicId(
  topicInternalId: number | null | undefined
): SubtopicOption[] {
  if (!topicHasSubtopics(topicInternalId)) return [];
  const raw = meta.subtopicsByTopicId[String(topicInternalId)];
  const opts = rawSubtopicsToOptions(raw);
  return [...opts].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
}

/**
 * Canonical sectionTitle subtopic shape for extract/compare/save:
 * - Removes legacy `subTopicName` / `subtopicName` from draft/API payloads.
 * - For topics without subtopics: `subtopicId` / `subtopicTitle` are empty arrays.
 * - For topics with subtopics: arrays only; drops ids not valid for that topic.
 */
export function normalizeSectionTitleSubtopicFields(
  data: Record<string, unknown>,
  topicInternalId: number | null | undefined
): void {
  delete (data as { subTopicName?: unknown }).subTopicName;
  delete (data as { subtopicName?: unknown }).subtopicName;

  if (!topicHasSubtopics(topicInternalId)) {
    data.subtopicId = [];
    data.subtopicTitle = [];
    return;
  }

  let ids = data.subtopicId;
  if (!Array.isArray(ids)) {
    ids = ids == null || ids === '' ? [] : [String(ids)];
  } else {
    ids = ids.map(String);
  }

  const allowed = new Set(
    getSubtopicsForTopicId(topicInternalId).map(o => o.value)
  );
  const filteredIds = (ids as string[]).filter(id => allowed.has(id));

  const opts = getSubtopicsForTopicId(topicInternalId);
  data.subtopicId = filteredIds;
  data.subtopicTitle = filteredIds.map(
    id => opts.find(o => o.value === id)?.label ?? ''
  );
}
